import { sponsorTypeFromWire } from '@/scheduling/lib/sponsor-type-wire';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type {
  AppointmentResponse,
  ParticipantResponse,
} from '@/types/scheduling-api';

/**
 * Hydrate the CreateAppointment form from an existing appointment for
 * EDIT mode — Flutter parity: `view_appointment_screen.dart` builds
 * `selectedAppointment` from `PreLoadAppointmentEvent` and hands it to
 * `CreateAppointmentWidget(isEdit: true)`.
 *
 * Scope: this seeds the IDENTITY fields (provider / patient / profile /
 * slot / location) and the fields the appointment OWNS directly
 * (status-derived, reasonCode, appointmentType, comment, reminder,
 * flags, recurrence, sponsor, follow-up). The create-panel sections
 * then auto-hydrate the DERIVED lists exactly as they do in create mode
 * once these IDs are present:
 *   - PreBookSelectedPatient → emergencyContacts, healthInsurances,
 *     corporateAgreements, addressList, patientFlags, invoiceTotals
 *   - PreBookSelectedAppointmentProfile → reasonCodeOptions,
 *     billingProfilesList (+ default reminder if none set)
 * So we deliberately do NOT map those here — seeding the IDs is enough,
 * and double-seeding would fight the sections' own hydration effects.
 *
 * `participant` carries provider + patient as separate entries keyed by
 * `referenceType` ('Provider' / 'Patient', case-insensitive on the wire).
 */

// The patient participant's `referenceType` is "Patient" for an adult but
// "RelatedPerson" for a guardian-managed (minor) patient (verified wire
// 2026-06-05). Matching only "patient" dropped the patient entry for minors —
// `patientId` came back null and the whole patient-gated UI (incl. the Payment
// section) silently vanished. Match both. Provider is "Provider"/"Practitioner".
const PARTICIPANT_TYPE_MATCHERS: Record<'provider' | 'patient', string[]> = {
  provider: ['provider', 'practitioner'],
  patient: ['patient', 'relatedperson'],
};

function findParticipant(
  participant: ParticipantResponse[] | undefined,
  type: 'provider' | 'patient',
): ParticipantResponse | undefined {
  const matchers = PARTICIPANT_TYPE_MATCHERS[type];
  const typeMatches = (p: ParticipantResponse) =>
    matchers.includes((p.referenceType ?? '').toLowerCase());
  // Prefer an entry that actually carries the expected sub-object (a
  // RelatedPerson entry without a `.patient` is a true related person, not the
  // patient), falling back to a referenceType match.
  const hasSub = (p: ParticipantResponse) =>
    type === 'patient' ? Boolean(p.patient) : Boolean(p.provider);
  // Tie-break is deterministic by participant array order: `.find` returns the
  // FIRST entry that both matches the type and carries the sub-object. The wire
  // ships exactly one patient participant (either "Patient" OR "RelatedPerson",
  // never both) and one provider, so the sub-object filter already isolates a
  // single entry; the first-in-order rule only matters for a malformed payload
  // with duplicates, where we pick the first rather than fail or pick randomly.
  return (
    participant?.find((p) => typeMatches(p) && hasSub(p)) ??
    participant?.find(typeMatches)
  );
}

export function appointmentToFormValues(
  appt: AppointmentResponse,
  /**
   * The full patient object from `PreLoadAppointmentDetails.patientData.patient`
   * — the appointment participant does NOT carry the patient's insurance /
   * corporate lists, so the Insurance/Corporate carousels came up empty in edit
   * (they otherwise only load via a PreBookSelectedPatient round-trip). Seeding
   * them here from data the page already has makes the lists appear immediately.
   */
  patientFull?: {
    healthInsuranceResponse?: AppointmentFormValues['healthInsurances'];
    corporateAgreementResponse?: AppointmentFormValues['corporateAgreements'];
  },
): Partial<AppointmentFormValues> {
  const providerEntry = findParticipant(appt.participant, 'provider');
  const patientEntry = findParticipant(appt.participant, 'patient');
  const provider = providerEntry?.provider;
  const patient = patientEntry?.patient;

  // Backend ships extra patient fields beyond PatientResponse on the
  // preload shape (patientMrn, phoneNumber, gender, age, importance).
  const patientLoose = patient as
    | {
        patientMrn?: string;
        mrn?: string;
        middleName?: string;
        phoneNumber?: string;
        gender?: string;
        age?: string;
        importance?: { code?: string };
        photoUrl?: string;
        dateOfBirth?: string;
        birthDate?: string;
      }
    | undefined;

  const providerLoose = provider as
    | { id?: string; name?: string; speciality?: unknown }
    | undefined;

  const patientName =
    [patient?.firstName, patientLoose?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(' ') || null;

  return {
    // --- Location ---
    location: appt.location
      ? { id: appt.location.id, name: appt.location.name }
      : appt.appointmentProfile?.location
        ? {
            id: appt.appointmentProfile.location.id,
            name: appt.appointmentProfile.location.name,
          }
        : null,

    // --- Provider (read-only in edit, but seeded so the card renders) ---
    providerId: providerLoose?.id ?? null,
    providerName: providerLoose?.name ?? null,
    providerSpeciality:
      (appt.specialty?.display as string | undefined) ?? null,
    // Full SNOMED speciality wire shape — the backend looks the
    // appointment up by `code.code`, so seed it verbatim rather than
    // letting a re-pick reconstruct it.
    providerSpecialityCode: appt.specialty ?? null,

    // --- Date & Time / slot ---
    selectedDate: appt.start ?? null,
    slotStart: appt.start ?? null,
    slotEnd: appt.end ?? null,
    // The detail response carries the resolved slot; fall back to a
    // synthetic id from start so the schema's `slotId` required-rule
    // passes (a loaded appointment always has a confirmed slot).
    slotId: appt.slot?.id ?? appt.start ?? null,

    // --- Appointment Profile (full snapshot → pickedProfile) ---
    // Flutter prefers `referenceId` (the profile TEMPLATE id used for
    // re-fetch) over the instance `id`. The React type doesn't expose
    // referenceId, but the wire carries it — read it loosely, fall back
    // to `id`.
    appointmentProfileId:
      (appt.appointmentProfile as { referenceId?: string } | undefined)
        ?.referenceId ??
      appt.appointmentProfile?.id ??
      null,
    appointmentProfileName: appt.appointmentProfile?.profileName ?? null,
    appointmentProfileColor: appt.appointmentProfile?.color ?? null,
    pickedProfile: appt.appointmentProfile ?? null,
    appointmentType: appt.appointmentType
      ? {
          code: appt.appointmentType.code,
          display: appt.appointmentType.display,
          definition: appt.appointmentType.definition ?? null,
        }
      : null,
    reasonCode: appt.reasonCode
      ? {
          code: appt.reasonCode.code,
          display: appt.reasonCode.display,
          definition: appt.reasonCode.definition ?? null,
        }
      : null,
    minutesDuration: appt.minutesDuration ?? null,

    // --- Patient (identity; PreBookSelectedPatient hydrates the rest) ---
    patientId: patient?.id ?? null,
    patientName,
    patientPhoto: patientLoose?.photoUrl ?? null,
    patientDob: patientLoose?.dateOfBirth ?? patientLoose?.birthDate ?? null,
    patientAge: patientLoose?.age ?? null,
    patientGender: patientLoose?.gender ?? null,
    patientImportanceCode: patientLoose?.importance?.code ?? null,

    // --- Payment / sponsor (always editable in edit — no status gate) ---
    // Wire ships "Self-pay" (lowercase p); the form enum is "Self-Pay".
    // Map back so the schema's enum validation passes (else Save stays
    // disabled). Inverse of the overlay's "Self-Pay"→"Self-pay".
    sponsorType: sponsorTypeFromWire(appt.sponsorType),
    sponsorId: appt.sponsor?.id ?? null,
    sponsor: appt.sponsor
      ? {
          id: appt.sponsor.id,
          payerName: (appt.sponsor as { payerName?: string }).payerName,
          name: (appt.sponsor as { name?: string }).name,
          imageUrl: (appt.sponsor as { imageUrl?: string }).imageUrl,
          // Keep the payer code + TPAs on the form so an edit that does NOT
          // re-pick the payer still ships a COMPLETE sponsor. The loaded
          // top-level sponsor can be sparse, so fall back to the invoice's
          // full sponsor. Without this the save stores payerCompanyId=null and
          // eRx→DHPO fails (empty ReceiverID/PayerID).
          payerCompanyId:
            (appt.sponsor as { payerCompanyId?: string | null }).payerCompanyId ??
            (appt as { invoice?: { sponsor?: { payerCompanyId?: string | null } } }).invoice
              ?.sponsor?.payerCompanyId ??
            null,
          relatedTpa:
            (appt.sponsor as { relatedTpa?: unknown[] | null }).relatedTpa ??
            (appt as { invoice?: { sponsor?: { relatedTpa?: unknown[] | null } } }).invoice
              ?.sponsor?.relatedTpa ??
            null,
        }
      : null,
    policyNo: appt.policyNo ?? null,

    // --- Insurance / Corporate carousels (Insurance/Corporate sponsor tabs).
    //     Seeded from the page's PreLoad patient; PreBookSelectedPatient still
    //     refreshes/overwrites them, but this avoids the empty-list flash and
    //     the case where that round-trip doesn't populate them. ---
    healthInsurances: patientFull?.healthInsuranceResponse ?? [],
    corporateAgreements: patientFull?.corporateAgreementResponse ?? [],

    // --- Note ---
    comment: appt.comment ?? null,

    // --- Reminder (appointment-owned id; name + rules auto-hydrate via
    //     PreBookSelectedAppointmentProfile, same as create). The
    //     CreateReminderProfileRequest wire only carries `id`. ---
    reminderProfileId: appt.reminderProfile?.id ?? null,

    // --- Questionnaire / form ---
    questionnaireId:
      (appt.questionnaireForm as { questionnaireId?: string } | undefined)
        ?.questionnaireId ?? null,
    questionnaireName:
      (appt.questionnaireForm as { name?: string } | undefined)?.name ?? null,
    questionnaireStatus:
      (appt.questionnaireForm as { status?: string } | undefined)?.status ??
      null,

    // --- Flags ---
    flagIds:
      appt.flagList
        ?.map((f) => String((f as { code?: string | number }).code ?? ''))
        .filter(Boolean) ?? [],

    // --- Recurrence ---
    recurring: appt.recurring
      ? {
          startDate: appt.recurring.startDate,
          endDate: appt.recurring.endDate,
          repeatEvery: appt.recurring.repeatEvery,
          numberOfRepeat: appt.recurring.numberOfRepeat,
          period:
            (appt.recurring.period as 'Days' | 'Weeks' | 'Months' | 'Years') ??
            'Weeks',
          days: appt.recurring.days,
        }
      : null,

    // --- Home visit / contacts (ids; lists auto-hydrate) ---
    residentialAddressId: appt.residentialAddressId ?? null,
    callbackContactId: appt.callBackContractId ?? null,

    // --- Priority / description ---
    priority: appt.priority ?? 0,
    description: appt.description ?? '',
  };
}
